/**
 * Email utility functions for the multi-email feature
 */

/**
 * Normalizes an email address for consistent storage and comparison
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Validates email format using the same regex as the database constraint
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email);
}

/**
 * Generates a suggested label for an email based on its domain
 */
export function generateEmailLabel(email: string): string {
  const domain = email.split('@')[1]?.toLowerCase();
  
  if (!domain) return 'Personal';
  
  // Common personal email domains
  const personalDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
    'aol.com', 'icloud.com', 'me.com', 'mac.com', 'live.com',
    'msn.com', 'mail.com', 'protonmail.com', 'proton.me'
  ];
  
  // Common work-related indicators
  const workIndicators = ['.edu', '.gov', '.org'];
  
  if (personalDomains.includes(domain)) {
    return 'Personal';
  }
  
  if (workIndicators.some(indicator => domain.endsWith(indicator))) {
    if (domain.endsWith('.edu')) return 'School';
    if (domain.endsWith('.gov')) return 'Government';
    return 'Work';
  }
  
  // Default to work for custom domains
  return 'Work';
}

/**
 * Formats email display with optional label
 */
export function formatEmailDisplay(email: string, label?: string): string {
  if (label) {
    return `${email} (${label})`;
  }
  return email;
}

/**
 * Gets email status display text
 */
export function getEmailStatusText(isVerified: boolean, isActive: boolean, bounceType?: string): string {
  if (!isVerified) {
    return 'Pending verification';
  }
  
  if (!isActive && bounceType) {
    return `Email bounced (${bounceType})`;
  }
  
  if (!isActive) {
    return 'Email inactive';
  }
  
  return 'Active';
}

/**
 * Checks if an email can be removed (non-primary emails only)
 */
export function canRemoveEmail(isPrimary: boolean): boolean {
  return !isPrimary;
}

/**
 * Gets email status color for UI
 */
export function getEmailStatusColor(isVerified: boolean, isActive: boolean): string {
  if (!isVerified) {
    return 'text-yellow-600'; // Pending
  }
  
  if (!isActive) {
    return 'text-red-600'; // Bounced/Inactive
  }
  
  return 'text-green-600'; // Active
}

/**
 * Formats relative time for last used display
 */
export function formatLastUsed(lastUsedAt?: string): string {
  if (!lastUsedAt) return 'Never used';
  
  const date = new Date(lastUsedAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes < 5) return 'Just now';
      return `${diffMinutes} minutes ago`;
    }
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }
  
  const months = Math.floor(diffDays / 30);
  if (months < 12) {
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  
  return date.toLocaleDateString();
}